'use client';
import { Flex } from '@chakra-ui/react';
import { AnimatePresence, motion, type Variants } from 'motion/react';
import React, { useRef, useEffect } from 'react';
import type { ComponentProps, ReactNode } from 'react';

const MODAL_CONTENT_VARIANTS: Variants = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
  },
  exit: {
    opacity: 0,
  },
};

const MotionFlex = motion.create(Flex);

export type FramerModalProps = {
  /**
   *
   */
  isOpen: boolean;

  /**
   * when modal is unmounted
   */
  onExit?: () => void;

  /**
   * modal content
   */
  children: ReactNode;

  /**
   * delay time after animation completion and onClose call
   * @default 150
   */
  exitDelay?: number;

  /**
   * The aria-label for the modal
   */
  ariaLabel?: string;
} & ComponentProps<typeof MotionFlex>;

const FramerModal = ({
  isOpen,
  onExit,
  children,
  backgroundColor = 'white',
  zIndex = 'modal',
  variants = MODAL_CONTENT_VARIANTS,
  exitDelay = 150,
  ariaLabel = 'Modal dialog',
  ...restProps
}: FramerModalProps) => {
  const prevIsOpenRef = useRef(isOpen);
  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen) {
      const originalOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';

      return () => {
        document.body.style.overflow = originalOverflow;
      };
    }
  }, [isOpen]);

  // Trap focus within modal when open
  useEffect(() => {
    if (isOpen && modalRef.current) {
      // Focus the first focusable element in the modal
      const focusableElements = modalRef.current.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
      );
      if (focusableElements.length > 0) {
        (focusableElements[0] as HTMLElement).focus();
      }
    }
  }, [isOpen]);

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | null = null;

    if (isOpen !== prevIsOpenRef.current) {
      if (!isOpen) {
        timer = setTimeout(() => onExit?.(), exitDelay);
      }
      prevIsOpenRef.current = isOpen;
    }

    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [isOpen, exitDelay, onExit]);

  return (
    <AnimatePresence mode="wait">
      {isOpen && (
        <MotionFlex
          height="100dvh"
          width="100dvw"
          position="fixed"
          flexDirection="column"
          top="0"
          left="0"
          zIndex={zIndex}
          bg={backgroundColor}
          initial="hidden"
          animate="show"
          exit="exit"
          variants={variants}
          role="dialog"
          aria-modal="true"
          aria-label={ariaLabel}
          tabIndex={-1}
          ref={modalRef}
          {...restProps}
        >
          {children}
        </MotionFlex>
      )}
    </AnimatePresence>
  );
};

export default FramerModal;
